
import { useState, useEffect } from 'react';
import { Notification, createNotificationId, getDaysWord } from './types';
import { useCourierInfo } from '@/pages/courier/hooks/useCourierInfo';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { addNotification } from '@/services/notificationService';

// Количество дней до платежа, когда нужно показать уведомление
const NOTIFICATION_DAYS = [1, 3, 5]; // За 1, 3 и 5 дней

// Функция для расчета дней до следующего платежа
const getDaysUntilPayment = (nextPaymentDate: string | Date | undefined): number | null => {
  if (!nextPaymentDate) return null;
  
  const now = new Date();
  const paymentDate = new Date(nextPaymentDate);
  
  // Обнуляем время для корректного сравнения только дат
  now.setHours(0, 0, 0, 0);
  paymentDate.setHours(0, 0, 0, 0);
  
  // Вычисляем разницу в днях
  const diffTime = paymentDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

export function useSubscriptionNotifications(): Notification[] {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { courierId } = useCourierInfo();
  const { user, isAdmin } = useAuth();
  
  useEffect(() => {
    // Для администраторов нам нужно загрузить все скоро истекающие подписки
    const loadSubscriptionNotifications = async () => {
      try {
        let query = supabase.from('subscriptions').select(`
          *,
          rentals:rental_id (
            id,
            human_number, 
            courier_id,
            couriers:courier_id (name, phone)
          )
        `).eq('status', 'active');

        // Для обычных пользователей фильтруем только их подписки
        if (!isAdmin && courierId) {
          query = query.filter('rentals.courier_id', 'eq', courierId);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error('Ошибка при получении подписок:', error);
          return;
        }
        
        if (!data || data.length === 0) {
          setNotifications([]);
          return;
        }
        
        // Преобразуем данные для генерации уведомлений
        const subscriptionAlerts: Notification[] = [];
        
        data.forEach((subscription: any) => {
          if (!subscription.next_payment_date) return;
          
          const daysUntil = getDaysUntilPayment(subscription.next_payment_date);
          
          // Если количество дней соответствует нашим триггерам для уведомлений
          if (daysUntil !== null && NOTIFICATION_DAYS.includes(daysUntil)) {
            // Для администратора добавляем информацию о курьере
            let title = 'Скоро списание по подписке';
            let message = '';
            
            if (isAdmin && subscription.rentals?.couriers) {
              title = `Скоро списание у ${subscription.rentals.couriers.name || 'курьера'}`;
              message = `Через ${daysUntil} ${getDaysWord(daysUntil)} произойдет списание ${subscription.amount} руб. по подписке для аренды №${subscription.rentals.human_number || subscription.rental_id}`;
            } else {
              message = `Через ${daysUntil} ${getDaysWord(daysUntil)} с вашей карты будет списано ${subscription.amount} руб. за аренду велосипеда`;
            }
            
            const notificationId = createNotificationId('subscription');
            const notification = {
              id: notificationId,
              title,
              message,
              timestamp: new Date().toISOString(),
              read: false,
              type: 'payment' as const
            };
            
            subscriptionAlerts.push(notification);
            
            // Добавляем в центральную систему уведомлений
            addNotification({
              id: notificationId,
              title,
              message,
              type: 'payment'
            });
          }
        });
        
        setNotifications(subscriptionAlerts);
      } catch (err) {
        console.error('Ошибка при проверке подписок:', err);
      }
    };
    
    // Загружаем данные только если пользователь авторизован
    if (user) {
      loadSubscriptionNotifications();
      
      // Обновляем каждый час
      const interval = setInterval(loadSubscriptionNotifications, 3600000);
      return () => clearInterval(interval);
    }
  }, [courierId, user, isAdmin]);
  
  return notifications;
}
